import facebook from '../services/facebook';

export default function(ngModule) {
    /**
     * @ngdoc service
     * @name eventix.common.models.OrderManager
     * @description
     * OrderManager model containing most of the logic to manage
     * reservations, metadata values and validation.
     *
     * Typically you'd initialize only 1 order on the page, and
     * it's initialized **after** `GET /:guid/data` using
     * `OrderManager.init()`.
     *
     * @param {Object} events Events keyed by `guid` (Like in `Event#cached`)
     * @param {Object} tickets Ticket arrays keyed by `event_guid`
     * <pre>
     * {
     *      'event_guid': [Ticket, Ticket]
     * }
     * </pre>
     * @param {Object} products Product arrays keyed by `ticket_guid`
     * <pre>
     * {
     *      'ticket_guid': [Product, Product]
     * }
     * </pre>
     * @param {Object[]} shopMetaData MetaData array that belongs to
     * shop - should be asked of a receiver
     */
    ngModule.factory('OrderManager', function ($translate, $timeout, Event, EventDate, Ticket, Product, MetaData, $q, Currencies, UIMessages, ErrorRejector,
                                               $state, $http, TicketReservation, LaravelValidationRules, BCAPIMessages, PaymentMethod, Locales, Shop,
                                               $filter, $rootScope, $analytics, $interval, VisitorData, OAuthTokens, ShopCoupon, StripeSDK, Rollbar, FB, store) {
        const Rules = LaravelValidationRules;

        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#quantities
         * @propertyOf eventix.common.models.OrderManager
         * @description Quantities of tickets reserved
         * <pre>
         * {
         *      'ticket_guid': 1
         * }
         * </pre>
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#reservations
         * @propertyOf eventix.common.models.OrderManager
         * @description Arrays of Reservations keyed by ticket_guid
         * <pre>
         * {
         *      'ticket_guid': [TicketReservation, TicketReservation]
         * }
         * </pre>
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#receiver
         * @propertyOf eventix.common.models.OrderManager
         * @description Object containing mainbooker details
         * <pre>
         * {
         *      firstname: '',
         *      lastname: '',
         *      email: ''
         *      $errors: {
         *          name: [], // Sum of first- and lastname errors
         *          firstname: [],
         *          lastname: [],
         *          email: []
         *      }
         * }
         * </pre>
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#subtotal
         * @propertyOf eventix.common.models.OrderManager
         * @description Subtotal price per `event_id`
         * <pre>
         * {
         *      event_id: 9000 // in cents
         * }
         * </pre>
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#instance
         * @propertyOf eventix.common.models.OrderManager
         * @description A reference to the instantiated OrderManager (since you're supposed to use this as a singleton service during the order process)
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#orderTotal
         * @propertyOf eventix.common.models.OrderManager
         * @description Total price (in cents) over all events, in the customer's selected currency.
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#tickets
         * @propertyOf eventix.common.models.OrderManager
         * @description Tickets keyed by event
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#events
         * @propertyOf eventix.common.models.OrderManager
         * @description Events keyed by event_id
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#firstReservation
         * @propertyOf eventix.common.models.OrderManager
         * @description Oldest reservation
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#products
         * @propertyOf eventix.common.models.OrderManager
         * @description Products keyed by ticket
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#paymentErrors
         * @kind Object<PaymentMethodGuid,String>
         * @propertyOf eventix.common.models.OrderManager
         * @description API errors relating to payment
         */

        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#receiverMeta
         * @propertyOf eventix.common.models.OrderManager
         * @description Map of `shopMetaData#guid` and a value user has entered
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.OrderManager#shopMetaData
         * @propertyOf eventix.common.models.OrderManager
         * @description Array of metadata applicable to receiver
         */

        function OrderManager(shop, events, tickets, products, shopMetaData) {
            const om = this;
            om.quantities = {};
            om.reservations = {};
            om.globalProductReservations = []
            om.globalProductsProductValues = []
            om.globalProductsPersonValues = []

            om.receiver = {};
            om.receiverMeta = {};
            om.subtotal = {};
            om.paymentErrors = {};

            om.couponError = null;
            om.orderTotal = 0;
            om.minPaymentCostTotal = 0;
            om.hasBookingFees = false;
            om.showFromStorageMsg = false;

            om.shop = shop;
            om.events = events;
            om.tickets = tickets;
            om.products = products;
            om.minPaymentMethod = null;
            om.shopMetaData = shopMetaData;
            _.forEach(_.flatten(_.values(tickets)), ticket => {
                om.reservations[ticket.guid] = [];
                om.quantities[ticket.guid] = 0;
            });

            // Do we have some receiver info?
            let rfirstName = store.get("receiver.firstname")
            if (!_.isNil(rfirstName))
                om.receiver.firstname = rfirstName;

            let rlastName = store.get("receiver.lastname")
            if (!_.isNil(rlastName))
                om.receiver.lastname = rlastName;

            VisitorData.emailFromStorage = false;
            let remail = store.get("receiver.email")
            if (!_.isNil(remail)) {
                VisitorData.emailFromStorage = true;
                om.showFromStorageMsg = true;
                om.receiver.email = remail;
            }

            _.forEach(shopMetaData, meta => {
                let oMetaData = store.get("receiver.metadata." + meta.guid)
                if (!_.isNil(oMetaData))
                    om.receiverMeta[meta.guid] = oMetaData;

                if (meta.type === 'boolean') {
                    om.receiverMeta[meta.guid] = parseInt(meta.default, 10) || 0;
                    return;
                }
                if (_.isNil(meta.default) || meta.default === '')
                    return;
                om.receiverMeta[meta.guid] = meta.default;
            });

            _.forEach(events, (event, guid) => om.subtotal[guid] = 0);
            om.checkReservationInterval = $interval(om.checkReservations.bind(om), 10000);
            $rootScope.$on('TicketReservationChanged', () => om.updateOrderTotal());
            $rootScope.$on('GlobalProductReservationsChanged', () => om.updateOrderTotal());
            $rootScope.$on('GlobalProductReservationsChanged', () => om.fixGlobalProductMetaData());
            // Copy values does not work and breaks same metadata usage in both order and ticket
            // $rootScope.$watch(() => om.receiver, copyPropertiesFromReceiverToReservation, true);
            // $rootScope.$watch(() => om.receiverMeta, copyPropertiesFromReceiverToReservation, true);
            OrderManager.instance = om;

            om.initializeFees();

            return om;
        }

        function copyPropertiesFromReceiverToReservation(newValues, oldValues) {
            const reservation = _.chain(OrderManager.instance.reservations)
                .values().flatten().first().value();
            if (!reservation)
                return;
            const productIds = _.map(reservation.products, 'guid');
            const currentValues = reservation.productReservations.getPersonMetaDataValuesFor(...productIds);
            const metaData = OrderManager.instance.shopMetaData.slice();
            metaData.push(...MetaData.cached.where(m => /^(fullname|first_name|last_name|email)$/.test(m.name)));
            _.forEach(metaData, meta => {
                if (!meta.public || meta.applies_to !== 'person')
                    return;

                switch (meta.name) {
                    // These will only be applicable for changes on vm.order.receiver
                    case 'fullname':
                        let ofullname = _.compact([oldValues.firstname, oldValues.lastname]).join(' ');
                        let nfullname = _.compact([newValues.firstname, newValues.lastname]).join(' ');
                        if (_.isEmpty(nfullname))
                            break;
                        if (_.isEmpty(currentValues[meta.guid]) ||
                            currentValues[meta.guid] === ofullname)
                            reservation.productReservations.setMetaDataValue(meta.guid, nfullname, productIds);
                        break;
                    case 'first_name':
                    case 'last_name':
                    case 'email':
                        let prop = meta.name.replace('_', '');
                        if (_.isEmpty(newValues[prop]))
                            break;
                        if (_.isEmpty(currentValues[meta.guid]) ||
                            currentValues[meta.guid] === oldValues[prop])
                            reservation.productReservations.setMetaDataValue(meta.guid, newValues[prop], productIds);
                        break;
                    // These will only be applicable for changes on vm.order.receiverMeta
                    case 'phonenumber':
                    case 'age':
                    case 'date_of_birth':
                    case 'gender':
                    case 'street':
                    case 'street_number':
                    case 'street_number_additional':
                    case 'postal':
                    case 'city':
                    case 'province':
                    case 'country':
                        if (_.isEmpty(newValues[meta.guid]))
                            break;
                        if (_.isEmpty(currentValues[meta.guid]) ||
                            currentValues[meta.guid] === oldValues[meta.guid])
                            reservation.productReservations.setMetaDataValue(meta.guid, newValues[meta.guid], productIds);
                        break;
                    default:
                        return;
                }
            });
        }

        (() => {
            BCAPIMessages.apply(OrderManager);
            OrderManager.prototype.broadcastAPIMessage = OrderManager.__broadcastAPIMessage;

            OrderManager.configureAPIMessages({
                '/:guid/reserve/ticket/:tguid': {
                    400: 'models.ticket.notice.soldOut',
                    406: 'models.ticket.notice.maxReserved'
                },
                '/:guid/reserve/product/:tguid': {
                    400: 'models.product.notice.soldOut',
                    406: 'models.ticket.notice.maxReserved'
                },
                '/:guid/order': {
                    200: 'common.shop.order.to_bank',
                    406: 'models.shop.notice.orderInvalid'
                }
            });
        })();

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#init
         * @methodOf eventix.common.models.OrderManager
         * @static
         * @description
         * Initialize an order with model data. Make sure to call this **after** populating Event, Ticket and
         * Product caches with `GET /:guid/data`. It'll build the grouped tickets and products objects.
         * @return {OrderManager} A new OrderManager
         */
        OrderManager.init = function () {
            const cacheOnly = true;
            var tickets, products, metaData, collapses;
            var events = _.keyBy(_.sortBy(Event.cached, 'start'), 'guid');
            var shop = Shop.cached.first();


            return shop.$queryTicket(cacheOnly).then(tickets => {
                tickets = $filter('sortByPredecessor')(tickets);
                return _.map(events, event => {
                    return [event.guid, _.filter(tickets, ticket => {
                        return event.belongsToMany.isCached('Ticket', ticket);
                    })];
                });
            }).then(ticketsByEventId => {
                tickets = _.fromPairs(ticketsByEventId);
                var flatlist = _.flatten(_.values(tickets));
                var productPromises = _.map(flatlist, ticket => {
                    return ticket.$queryProduct(cacheOnly).then(products => [ticket.guid, products]);
                });
                var ticketPromises = _.map(flatlist, ticket => ticket.calculateMinimumPrice());
                return $q.all(ticketPromises).then(() => $q.all(productPromises));
            }).then(productsByTicketId => {
                products = _.fromPairs(productsByTicketId);
                return shop.$queryCollapse(cacheOnly);
            }).then(queriedCollapses => {
                collapses = queriedCollapses;
                return shop.$queryMetaData(cacheOnly);
            }).then(meta => {
                metaData = meta;
                return $q.resolve(new OrderManager(shop, events, tickets, products, metaData, collapses));
            });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#reserveTicket
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Reserve a ticket
         *
         * Creates a reservation, updates the quantities and (sub)totals
         * @param {String} guid Ticket GUID
         * @param {String} eventId Event GUID
         * @return {TicketReservation} Promise that resolves with the reservation created
         */
        OrderManager.prototype.reserveTicket = function (guid, eventId) {
            const om = this;
            let ticket = Ticket.cached[guid];
            if (ticket.status !== 'available')
                return $q.reject('models.ticket.notice.' + ticket.status);
            if (om.quantities[guid] + ticket.increment > ticket.max_orderable_amount_per_order) {
                UIMessages.push('models.ticket.notice.maxOrdered');
                return $q.reject('models.ticket.notice.maxOrdered');
            }
            let reserveQueueLimit = _.get(om, ['reserveQueueLimit', guid], 5);
            if (om.reserveQueue && om.reserveQueue.length >= reserveQueueLimit) {
                UIMessages.push('common.notice.pleaseWait');
                return $q.reject('common.notice.pleaseWait');
            }
            let incrementor = om.quantities[guid] === 0 ? Math.max(ticket.min_orderable_amount_per_order, ticket.increment) : ticket.increment;
            incrementor = ticket.seats_category_key ? 1 : incrementor;
            om.quantities[guid] += incrementor;
            om.busy = true;
            om.reserveQueue = om.reserveQueue || [];
            var promises = _.times(incrementor, () => {
                let promise = $http.get(`/${$state.params.shopId}/reserve/ticket/${guid}`)
                    .then(response => {
                        _.pull(om.reserveQueue, promise);
                        om.broadcastAPIMessage(response);
                        _.assign(response.data, {
                            ticket_guid: guid,
                            event_guid: eventId
                        });
                        var r = TicketReservation.new(response.data);
                        if (!om.firstReservation)
                            om.firstReservation = r;
                        om.reservations[guid].push(r);
                        _.set(om, ['reserveQueueLimit', guid], 5);
                        return r.findingModels;
                    }, err => {
                        _.set(om, ['reserveQueueLimit', guid], 0);
                        $timeout(() => {
                            _.set(om, ['reserveQueueLimit', guid], 5);
                        }, 750);
                        _.pull(om.reserveQueue, promise);

                        if (_.get(err, 'data.error_description') === 'Sold out') {
                            $rootScope.$broadcast('APIMessage', 400, 'models.ticket.notice.soldOut');

                            ticket.status = 'sold_out';
                        } else {
                            om.broadcastAPIMessage(err);
                        }

                        om.quantities[guid]--;
                    });
                om.reserveQueue.push(promise);
                return promise;
            });
            return $q.all(promises).then(() => {
                om.updateOrderTotal();
                om.busy = false;
                let incrementOverflow = om.quantities[guid] % incrementor;
                let promises = _.times(incrementOverflow, () => om.releaseTicket(guid, eventId));
                return $q.all(promises).then(() => om);
            });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#releaseTicket
         * @methodOf eventix.common.models.OrderManager
         * @description Releases a ticket (LIFO)
         * @param {String|Object} target Ticket GUID or a specific reservation
         * @param {String} eventId Event GUID
         * @param {Boolean} [sync=false] Whether to perform async HTTP call.
         * In some cases you'll want a synchronous call. In that case a raw XHR request is
         * created instead of the `$http` service
         * @return {TicketReservation} Promise that resolves with the ticket that was released
         */
        OrderManager.prototype.releaseTicket = function (target, eventId, sync = false) {
            const om = this;
            if (om.reserveQueue && om.reserveQueue.length > 0) {
                UIMessages.push('common.notice.pleaseWait');
                return $q.reject('common.notice.pleaseWait');
            }
            om.busy = true;
            var promises = [];
            var ticket, guid;
            if (angular.isString(target)) {
                guid = target;
                ticket = Ticket.cached[guid];
                let decrementor = om.quantities[guid] === ticket.min_orderable_amount_per_order ? ticket.min_orderable_amount_per_order : ticket.increment;
                decrementor = ticket.seats_category_key ? 1 : decrementor;
                promises = _.times(decrementor, () => {
                    if (om.quantities[guid] - 1 < 0)
                        return $q.resolve();
                    var reservation = om.reservations[guid].pop();
                    return release(reservation);
                });
            } else {
                guid = target.ticket_guid;
                ticket = target.ticket;
                _.pull(om.reservations[guid], target);
                promises.push(release(target));
            }

            return $q.all(promises).then(() => {
                om.checkReservations();
                om.updateOrderTotal();
                om.busy = false;
                return ticket;
            });

            function release(reservation) {
                if (!reservation)
                    return $q.resolve();
                if (om.firstReservation === reservation)
                    om.firstReservation = null;
                om.quantities[guid]--;
                return reservation.release(sync).catch(() => {
                    om.quantities[guid]++;
                    om.reservations[guid].push(reservation);
                });
            }
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#updateOrderTotal
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Calculate the total order value in the customer's selected currency. Because events in the shop can be
         * using different currencies, they need to be exchanged.
         *
         * @return {Number} Total order value
         */
        OrderManager.prototype.updateOrderTotal = function () {
            const om = this;
            _.forEach(om.events, (event, guid) => om.subtotal[guid] = 0);
            if (om.coupon) {
                let flatList = _.flatten(_.values(om.reservations));
                om.coupon.reapplyAcrossReservations(flatList);
                let allTickets = _.flatten(_.values(om.tickets));
                _.forEach(allTickets, t => t.calculateDiscountPrice(om.coupon));
            }
            _.forEach(om.reservations, reservations => {
                _.forEach(reservations, reservation => {
                    reservation.calculatePrice(om.coupon);
                    om.subtotal[reservation.event_guid] += reservation.price;
                });
            });

            om.orderTotal = _.reduce(om.subtotal, (total, sub) => {
                return total + Currencies.exchange(sub, om.shop.currency, Currencies.selected);
            }, 0);

            _.forEach(om.globalProductReservations, reservation => {
                let product = Product.cached[reservation.product_guid]
                // First the non global products
                if(!product.isContainerProduct) {
                    om.orderTotal += reservation.calculatePrice(om);
                    // TODO: Coupon?
                }
            });

            om.orderTotalForGlobalProducts = 0;
            om.orderTotalForGlobalProducts = om.orderTotal;
            _.forEach(om.globalProductReservations, reservation => {
                let product = Product.cached[reservation.product_guid]
                // Then the global products
                if(product.isContainerProduct) {
                    om.orderTotal += reservation.calculatePrice(om);
                    // TODO: Coupon?
                }
            });

            om.calculateMinPaymentCosts();
            if (om.minPaymentMethod && om.orderTotal > 0)
                om.orderTotal += om.paymentCost(om.paymentProvider || om.minPaymentMethod);
            return om.orderTotal;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#checkReservations
         * @methodOf eventix.common.models.OrderManager
         * @emits reservation_expired
         * @description
         * Loop over the reservations and see if any of them are expired.
         * Will also set the `om.firstReservation` to the oldest reservation
         */
        OrderManager.prototype.checkReservations = function () {
            let om = this;
            let now = moment().format('YYYY-MM-DDTHH:mm:ssZ');
            let fallback = moment().add(1, 'day').format('YYYY-MM-DDTHH:mm:ssZ');
            _.forEach(this.reservations, (reservations, ticketId) => {
                let expired = _.remove(reservations, r => r.expiration <= now);
                if (expired.length) {
                    $rootScope.$broadcast('reservation_expired');
                    om.quantities[ticketId] -= expired.length;
                }
                if (!reservations.length) return;
                let first = _.head(reservations);
                if (first.expiration < _.get(om.firstReservation, 'expiration', fallback))
                    om.firstReservation = first;
            });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#releaseAll
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Release all tickets for a blank start
         * @returns {Promise} Resolves when all reservations are released
         */
        OrderManager.prototype.releaseAll = function () {
            let om = this;
            let promises = _.map(om.quantities, (amount, ticketGuid) => {
                let eventId = _.findKey(om.tickets,
                    (ticketsInEvent, eventId) => _.findIndex(ticketsInEvent, {guid: ticketGuid}) > -1);
                let promises = _.times(amount, () => {
                    return om.releaseTicket(ticketGuid, eventId, true);
                });
                return promises;
            });
            return $q.all(_.flatten(promises));
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#releaseAllGlobalProductReservations
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Release all tickets for a blank start
         * @returns {Promise} Resolves when all reservations are released
         */
        OrderManager.prototype.releaseAllGlobalProductReservations = function () {
            let om = this;
            let promises = _.map(om.quantities, (amount, ticketGuid) => {
                let eventId = _.findKey(om.tickets,
                    (ticketsInEvent, eventId) => _.findIndex(ticketsInEvent, {guid: ticketGuid}) > -1);
                let promises = _.times(amount, () => {
                    return om.releaseTicket(ticketGuid, eventId, true);
                });
                return promises;
            });
            return $q.all(_.flatten(promises));
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#reset
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Reset the order to starting condition
         *
         * *Warning* - Deletes reservations without releasing them on the API
         */
        OrderManager.prototype.reset = function () {
            const om = this;
            om.quantities = {};
            om.reservations = {};
            om.receiver = {};
            om.receiverMeta = {};
            om.subtotal = {};
            om.paymentErrors = {};
            om.orderTotal = 0;
            _.forEach(_.flatten(_.values(om.tickets)), ticket => {
                om.reservations[ticket.guid] = [];
                om.quantities[ticket.guid] = 0;
            });
            _.forEach(om.events, (event, guid) => om.subtotal[guid] = 0);
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#validate
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Kick of validation logic of OrderManager and all TicketReservations
         * @return {Boolean} True if order is invalid
         */
        OrderManager.prototype.validate = function () {
            const om = this;
            let invalidReceiver = om.validateReceiver();
            return om.hasInvalidReservations() || invalidReceiver.length > 0;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#hasInvalidReservations
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Kick of validation logic of OrderManager and all TicketReservations
         * @return {Boolean} True if order is invalid
         */
        OrderManager.prototype.hasInvalidReservations = function () {
            const om = this;
            let invalids = _.map(om.reservations, reservations => {
                let invalids = _.map(reservations, reservation => {
                    const invalid = reservation.$invalid();
                    if(angular.isString(invalid))
                        UIMessages.push(invalid);
                    return !!invalid;
                });
                return _.compact(invalids).length > 0;
            });
            return _.compact(invalids).length > 0;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#validateReceiver
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Validate the receiver, name and email are required and should be valid.
         * @param {String[]} [attributes=all] Which receiver attributes should be validated
         * @return {String[]} Error messages
         */
        OrderManager.prototype.validateReceiver = function (attributes) {
            const om = this;
            attributes = attributes || ['email', 'name'].concat(_.map(om.shopMetaData, 'guid'));
            const fakeMeta = [{
                guid: 'email',
                name: 'email',
                translateName: 'common.metaData.email',
                extra: 'email|required'
            }, {
                guid: 'firstname',
                name: 'first name',
                translateName: 'common.metaData.first_name',
                extra: 'required'
            }, {
                guid: 'lastname',
                name: 'last name',
                translateName: 'common.metaData.last_name',
                extra: 'required'
            }];

            var data = _.merge({}, om.receiver, om.receiverMeta);
            var errors = Rules.validate(fakeMeta.concat(om.shopMetaData), data);
            errors.name = errors.firstname.concat(errors.lastname);
            om.receiver.$errors = _.pick(errors, attributes);
            if (om.shop.email_validation_rule) {
                let shopEmailRegex = om.shop.email_validation_rule.replace(/regex:\[/g, '');
                shopEmailRegex = new RegExp(shopEmailRegex.replace(/\]$/g, ''));
                if (!shopEmailRegex.test(om.receiver.email))
                    om.receiver.$errors.email.push('Email not valid for this shop');
            }
            // Lets store for future use
            let dataLayersKeys =["email", "firstname", "lastname"];
            _.forEach(om.receiver, (value, key) => {
                // push receiver to datalayer
                if(dataLayersKeys.includes(key)) {
                    let receiverData = {};
                    receiverData["receiver." + key] = value;
                    $analytics.eventTrack('ecommerce', receiverData);
                    store.set("receiver." + key, value)
                }
            });
            _.forEach(om.receiverMeta, (value, mGuid) => {
                store.set("receiver.metadata." + mGuid, value)
            });

            return _.flatten(_.values(om.receiver.$errors));
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#clearErrors
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Clear all error messages - this doesn't change validation state
         */
        OrderManager.prototype.clearErrors = function () {
            const om = this;
            om.receiver.$errors = {};
            om.paymentErrors = {};
            _.forEach(om.reservations, rs => {
                _.forEach(rs, r => {
                    r.$errors = {};
                });
            });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#submit
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Collect and format data from reservations and submit it to the API
         * @returns {Object} Promise that resolves with the $http response
         */
        OrderManager.prototype.submit = function () {
            const om = this;
            var beforeSubmit = $q.resolve();
            var data = {};
            var reservations = _.flatten(_.values(om.reservations));
            VisitorData.timeToBank = Math.abs(moment().diff(om.firstReservation.reservedOn, 'seconds'));
            data.receiver = _.pick(om.receiver, ['email', 'firstname', 'lastname']);
            data.tickets = _.map(reservations, r => r.prepareData());
            console.log(om.globalProductReservations);
            data.products = _.map(om.globalProductReservations, r => r.prepareData());
            data.paymentProvider = om.paymentProvider;
            data.paymentProviderIssuer = om.paymentProviderIssuer;
            data.currency = Currencies.selected;
            data.receiver.locale = Locales.selected;
            data.metaData = _.compact(_.map(om.receiverMeta, (value, guid) => {
                if (_.isNil(value) || value === '')
                    return null;
                return {guid, value};
            }));
            data.extraMetaData = VisitorData;
            if (om.seatsReservationToken)
                data.seatsReservationToken = om.seatsReservationToken;

            if (om.coupon)
                data.coupon = om.coupon.code;

            if (VisitorData.queryParams.hasOwnProperty('coupon'))
                data.coupon = VisitorData.queryParams.coupon;

            let stripeMethod = _.first(PaymentMethod.cached.where(p => p.isStripe()));
            if (om.stripe && stripeMethod && stripeMethod.guid === om.paymentProvider && om.orderTotal > 0) {
                beforeSubmit = beforeSubmit
                    .then(() => StripeSDK.get())
                    .then(stripe => stripe.createToken(om.stripe.card))
                    .then(result => {
                        if (result.error)
                            return $q.reject(om.stripe.error = result.error.message);
                        data.stripeToken = result.token;
                        return $q.resolve();
                    });
            }

            return beforeSubmit
                .then(() => {
                    return $http.post(`/${$state.params.shopId}/order`, data)
                        .then(response => {
                            om.broadcastAPIMessage(response);
                            return $q.resolve(response);
                        })
                        .catch(error => {
                            om.broadcastAPIMessage(error);
                            parseOrderErrorResponse.call(this, error, data, reservations);
                            return $q.reject(error);
                        });
                });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#showTicketReservationForm
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Test if user interaction is required for a ticket
         * @param {String} ticketId Ticket GUID
         * @returns {Boolean} True if the ticket has optional products or metadata which require user interaction
         */
        OrderManager.prototype.showTicketReservationForm = function showTicketReservationForm(ticketId) {
            const om = this;
            const ticket = Ticket.cached[ticketId];
            if (!ticket) return false;
            const groups = ticket.belongsToMany.restore('ProductGroup');
            const hasOptionalProducts = _.some(groups, group => !group.required);
            if (hasOptionalProducts) return true;
            const hasMetaData = _.some(om.products[ticketId], p => p.belongsToMany.restore('MetaData').length > 0);
            if (hasMetaData) return true;
            return false;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#loginWithFacebook
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Autocomplete metaData values with Facebook profile
         * @returns {Promise} Resolves when done
         */
        OrderManager.prototype.loginWithFacebook = function() {
            const om = this;
            return FB.login('public_profile,email,user_birthday,user_gender,user_location')
                .then(() => FB.api('/me?fields=id,email,first_name,last_name,gender,location,birthday'))
                .then(facebookData => {
                    // Facebook ID
                    VisitorData.facebookId = facebookData.id;

                    // Main receiver data - first_name
                    if (facebookData.first_name && facebookData.first_name.length) {
                        om.receiver.firstname = facebookData.first_name;
                    } else {
                        console.error('first_name not in facebook data');
                    }

                    // Main receiver data - last_name
                    if (facebookData.last_name && facebookData.last_name.length) {
                        om.receiver.lastname = facebookData.last_name;
                    } else {
                        console.error('last_name not in facebook data');
                    }

                    // Main receiver data - email
                    if (facebookData.email && facebookData.email.length) {
                        om.receiver.email = facebookData.email;
                    } else {
                        console.error('email not in facebook data');
                    }

                    // Reset errors so far
                    if (om.validate()) {
                        om.clearErrors();
                    }

                    // Postprocess facebook data - birthday
                    if (facebookData.birthday) {
                        // Response can be full date, only day or only year
                        if (facebookData.birthday.length >= 10) {
                            facebookData.birthday = moment(facebookData.birthday, 'MM/DD/YYYY');
                        } else if (facebookData.birthday.length === 5) {
                            facebookData.birthday = moment(facebookData.birthday, 'MM/DD');
                        } else if (facebookData.birthday.length === 4) {
                            facebookData.birthday = moment(facebookData.birthday, 'YYYY');
                        } else {
                            // Ignore anything else
                            facebookData.birthday = undefined;
                        }
                    }

                    // Postprocess facebook data - gender
                    if (facebookData.gender && facebookData.gender === 'female' && _.includes(metaData.extra, 'woman')) {
                        facebookData.gender = 'woman';
                    }

                    function getLocation() {
                        return FB.api(facebookData.location.id + '?fields=location')
                            .then(response => {
                                return {
                                    city: _.get(response, 'location.city', ''),
                                    country: _.get(response, 'location.country', '')
                                };
                            })
                            .catch(error => {
                                console.error('Facebook location data', error);

                                return $q.reject(error);
                            });
                    }

                    // Lazy singleton location data
                    let getLocationPromise;

                    // Process all data we have meta data for
                    _.forEach(_.filter(om.shopMetaData, m => !!m.auto_fill_facebook), metaData => {
                        switch (metaData.auto_fill_facebook) {
                            case 'city':
                                if (!getLocationPromise) {
                                    getLocationPromise = getLocation();
                                }

                                getLocationPromise.then(r => {
                                    if (r && r.city && r.city.length) {
                                        om.receiverMeta[metaData.guid] = r.city;
                                    } else {
                                        console.error('city not in facebook data');
                                    }
                                });

                                break;

                            case 'country':
                                if (!getLocationPromise) {
                                    getLocationPromise = getLocation();
                                }

                                getLocationPromise.then(r => {
                                    if (r && r.country && r.country.length) {
                                        om.receiverMeta[metaData.guid] = r.country;
                                    } else {
                                        console.error('country not in facebook data');
                                    }
                                });

                                break;

                            case 'gender':
                                if (facebookData.gender && facebookData.gender.length > 0) {
                                    om.receiverMeta[metaData.guid] = facebookData.gender;
                                } else {
                                    console.error('gender not in facebook data');
                                }

                                break;

                            case 'age':
                                if (facebookData.birthday && moment.isMoment(facebookData.birthday) && facebookData.birthday.isValid()) {
                                    om.receiverMeta[metaData.guid] = moment().diff(facebookData.birthday, 'years') + '';
                                } else {
                                    console.error('birthday (age) not in facebook data');
                                }

                                break;

                            case 'date_of_birth':
                                if (facebookData.birthday && moment.isMoment(facebookData.birthday) && facebookData.birthday.isValid()) {
                                    om.receiverMeta[metaData.guid] = facebookData.birthday.format('YYYY-MM-DD');
                                } else {
                                    console.error('birthday (dob) not in facebook data');
                                }

                                break;

                            default:
                                break;
                        }
                    });

                    return $q.resolve(facebookData);
                });
        };

        function parseOrderErrorResponse(errorResponse, requestData, reservations) {
            const om = this;
            let errorObj = _.get(errorResponse, 'data.errors');
            _.forEach(errorObj, (e) => {
                let reservation = _.find(reservations, {reservation: e.reservation});
                let subjectType = _.has(e, 'parentOf') ? _.get(e, 'parentOf.subjectType') : _.get(e, 'subjectType');
                let metaGuid, messages;
                switch (subjectType) {
                    case 'Product':
                        let productGuid = e.parentOf.guid;
                        metaGuid = e.parentOf.parentOf.guid;
                        messages = reservation.$errors[productGuid][metaGuid] || [];
                        let meta = MetaData.cached[metaGuid];
                        let reason = _.get(e, 'parentOf.parentOf.reason');
                        if (typeof reason === 'string') {
                            let attribute = $translate.instant(meta.translateName);
                            messages.push(
                                $translate.instant(
                                    e.parentOf.parentOf.reason,
                                    {attribute: attribute}
                                )
                            );
                        } else {
                            let reasonMessages = _.map(reason, r => {
                                let attribute = $translate.instant(meta.translateName);
                                return $translate.instant(r, {attribute: attribute});
                            });
                            messages = messages.concat(reasonMessages);
                        }
                        _.set(reservation.$errors, [productGuid, metaGuid], messages);
                        break;
                    case 'MetaData':
                        metaGuid = e.parentOf.guid;
                        messages = reservation.$errors[metaGuid] || [];
                        let attribute = $translate.instant(meta.translateName);
                        messages.push(
                            $translate.instant(
                                e.parentOf.reason,
                                {attribute: attribute}
                            )
                        );
                        reservation.$errors[metaGuid] = messages;
                        break;
                    case 'Payment':
                        UIMessages.push(e.reason);
                        om.paymentErrors[e.parentOf.guid] = [$translate.instant(e.reason)];
                        let method = PaymentMethod.cached[e.parentOf.guid];
                        if (_.has(method, 'stripe_public_key'))
                            $rootScope.$broadcast('stripe_payment_failed', e.reason);
                        break;
                    case 'Ticket':
                        if (e.reason === 'validation.extra.reservation.expired')
                            $rootScope.$broadcast('reservation_expired');
                        else
                            Rollbar.error(new Error(`Couldn't process error: ${JSON.stringify(e)}`));
                        break;
                    case 'Coupon':
                        ErrorRejector.handle(e.reason);
                        break;
                    default:
                        Rollbar.error(new Error(`Couldn't process error: ${JSON.stringify(e)}`));
                        break;
                }
            });
            errorObj = _.get(errorResponse, 'data.error_description');
            _.forEach(errorObj, (messages, path) => {
                if (/receiver/.test(path)) {
                    let receiverObject = path.replace(/receiver./g, '');
                    if (!_.isArray(om.receiver.$errors[receiverObject]))
                        om.receiver.$errors[receiverObject] = [];
                    om.receiver.$errors[receiverObject].push(messages);
                }

                if (!/^tickets[\.\d]+products[\.\d]+metaData/.test(path)) return;
                let productGuidPath = path.match(/tickets\.\d+\.products\.\d+/)[0] + '.guid';
                let metaGuidPath = path.match(/tickets\.\d+\.products\.\d+\.metaData\.\d+/)[0] + '.guid';
                let reservation = reservations[path.split('.')[1]];
                let productGuid = _.get(requestData, productGuidPath);
                let metaGuid = _.get(requestData, metaGuidPath);
                messages = _.map(messages, m => m.replace(path, MetaData.cached[metaGuid].name));
                _.set(reservation.$errors, [productGuid, metaGuid], messages);
            });
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#paymentCost
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Calculates payment cost for a given method
         *
         * @param {PaymentMethod|String} method A method or its GUID
         * @param {Number|null} price An order price (or null if orderTotal should be used)
         * @returns {Number} Total payment cost
         */
        OrderManager.prototype.paymentCost = function (method, price = null) {
            if (!method)
                return 0;

            const om = this;

            if (typeof method === 'string')
                method = PaymentMethod.cached[method];

            if (_.isNil(price)) {
                price = _.reduce(om.subtotal, (total, sub) => {
                    return total + Currencies.exchange(sub, om.shop.currency, Currencies.selected);
                }, 0);
            }

            if (price === 0)
                return 0;

            let totalPercentage = method.fees.self.percentages + method.fees.other.percentages;
            let totalFixed = method.fees.self.fixed + method.fees.other.fixed;

            return price * (totalPercentage / 100) + totalFixed;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#determineHasBookingFees
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Determines if all method fees are the same and Transaction fees can be seen as booking fees.
         *
         * This is especially important for Belgium customers
         */
        OrderManager.prototype.initializeFees = function () {
            const om = this;
            let methods = _.values(PaymentMethod.cached);

            if (!_.size(methods))
                return;

            if (_.size(methods) === 1) {
                om.hasBookingFees = true;

                return;
            }

            let firstFixed = _.get(_.head(methods), 'total_transaction_fixed', -2);
            let firstPercentage = _.get(_.head(methods), 'total_transaction_percentage', -2);

            om.hasBookingFees = methods.every(method => {
                return _.get(method, 'total_transaction_fixed', -1) === firstFixed &&
                    _.get(method, 'total_transaction_percentage', -1) === firstPercentage;
            });

            om.minPaidTicketPrice = _.chain(om.tickets)
                .values()
                .flatten('minimumPrice')
                .filter('minimumPrice')
                .minBy('minimumPrice')
                .get('minimumPrice', 0)
                .value();

            om.calculateMinPaymentCosts();
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#calculateMinPaymentCosts
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Calculates the minimal transaction costs (fixed)
         *
         * Also checks if all transaction methods are the same price
         * if so set hasBookingFees to true (we use this later on)
         * especially for Belgium customers
         */
        OrderManager.prototype.calculateMinPaymentCosts = function () {
            const om = this;

            if (!PaymentMethod.cached || !_.size(PaymentMethod.cached)) {
                om.minPaymentCostTotal = 0;

                return;
            }

            // If price = null, the OrderManager.paymentCost() method will use the orderTotal value.
            let price = null;

            if (_.isNil(om.firstReservation))
                price = om.minPaidTicketPrice;

            let minMethod = _.minBy(_.map(PaymentMethod.cached, method => {
                return {
                    payment_cost: om.paymentCost(method, price),
                    method: method
                };
            }), 'payment_cost');

            om.minPaymentMethod = minMethod.method;
            om.minPaymentCostTotal = minMethod.payment_cost;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#applyCoupon
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Adds a coupon to this order
         *
         * Checks if the code is valid and applies it to reservations and tickets in shop
         * @param {String} code Coupon code to be checked
         */
        OrderManager.prototype.applyCoupon = function (code) {
            const om = this;

            let shop = Shop.cached.first();

            om.couponError = null;

            return ShopCoupon.get(shop.guid, code)
                .then(coupon => {
                    om.coupon = coupon;
                    om.updateOrderTotal();
                    om.showRelevantTickets();
                    om.determineCouponUselessness();
                })
                .catch(ErrorRejector.handle)
                .catch(error => {
                    om.couponError = error;
                    return $q.reject(error);
                });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#removeCoupon
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Remove the coupon from this order
         *
         * Resets reservation prices and tickets in shop
         */
        OrderManager.prototype.removeCoupon = function () {
            const om = this;
            if (!om.coupon)
                return;
            om.coupon.clear();
            delete om.coupon;
            let allTickets = _.flatten(_.values(om.tickets));
            _.forEach(allTickets, t => delete t.discountPrice);
            om.updateOrderTotal();
            om.hideTicketsWithoutCoupons();
        };

        OrderManager.prototype.determineCouponUselessness = function () {
            const om = this;

            let allTickets = _.flatten(_.values(om.tickets));
            let couponProductGuids = _.map(_.get(om.coupon, 'coupon.products', []), 'guid');

            let valid = _.sumBy(allTickets, t => {
                let ticketProductGuids = _.map(t.belongsToMany.restore('Product'), 'guid');

                return _.size(_.intersection(ticketProductGuids, couponProductGuids)) > 0;
            });

            if (valid < 1) {
                om.couponError = 'validation.extra.coupon.useless';

                ErrorRejector.handle('validation.extra.coupon.useless');
            }
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.OrderManager#recalculateAvailableTickets
         * @methodOf eventix.common.models.OrderManager
         * @description
         * Calculate which tickets are available. Sets `event.availableTickets` to the number of tickets for said event
         *
         * @return {Number} Total number of available tickets
         */
        OrderManager.prototype.recalculateAvailableTickets = function () {
            const om = this;
            _.forEach(om.events, event => {
                var avail = _.filter(om.tickets[event.guid], ticket => ticket.isAvailable());
                om.events[event.guid].availableTickets = avail.length;
            });
            return _.sumBy(_.values(om.events), 'availableTickets');
        };

        OrderManager.prototype.showRelevantTickets = function () {
            const om = this;
            let allTickets = _.flatten(_.values(om.tickets));
            let couponProductGuids = _.map(_.get(om.coupon, 'coupon.products', []), 'guid');
            _.forEach(allTickets, t => {
                if (t.hide_without_coupon) {
                    let ticketProductGuids = _.map(t.belongsToMany.restore('Product'), 'guid');
                    if (_.size(_.intersection(ticketProductGuids, couponProductGuids)) > 0)
                        t.show_in_shop = true;
                    else
                        t.show_in_shop = false;
                }
            });
        };

        OrderManager.prototype.hideTicketsWithoutCoupons = function () {
            const om = this;
            let allTickets = _.flatten(_.values(om.tickets));
            _.forEach(allTickets, t => {
                if (t.hide_without_coupon)
                    t.show_in_shop = false;
            });
        };

        /**
         * Get the global product reservation count
         * @param {String} productGuid the product guid to filter on or null if no filter is desired.
         * @return {number} the amount of global product reservations found
         */
        OrderManager.prototype.getGlobalProductReservationCount = function (productGuid) {
            const om = this
            return productGuid
                ? om.globalProductReservations.filter(r => r.product_guid === productGuid).length
                : om.globalProductReservations.length
        }

        OrderManager.prototype.fixGlobalProductMetaData = function () {
            let reservations = this.globalProductReservations;
            let om = this;

            // build a metadata array with questions need to be asked and
            let PersonMetadatas = [];
            let ProductMetadatas = [];
            _.forEach(reservations, function (reservation) {
                ProductMetadatas[reservation.product_guid] = [];
            });
            _.forEach(reservations, function (reservation) {
                let product = Product.cached[reservation.product_guid]
                let productMetadatas = $filter('sortByPredecessor')(product.belongsToMany.restore('MetaData'));
                _.forEach(productMetadatas, function (meta) {
                    if (meta.applies_to === 'person') {
                        if (_.findIndex(PersonMetadatas, {guid: meta.guid}) == -1)
                            PersonMetadatas.push(meta);
                    }
                    if (meta.applies_to === 'product') {
                        if (_.findIndex(ProductMetadatas[reservation.product_guid], {guid: meta.guid}) == -1)
                            ProductMetadatas[reservation.product_guid].push(meta);
                    }
                });
            });

            om.globalPersonMetadatas = PersonMetadatas;
            om.globalProductMetadatas = ProductMetadatas;
        }

        return OrderManager;
    });
}
